'use client';

import React, { useState, useMemo } from 'react';

import { CheckSquare, Plus, Search, ChevronDown, ChevronUp, Clock, CheckCircle2, AlertCircle, Circle, XCircle, Wheat, Stethoscope, Sparkles, Syringe, Egg, Scale, Shield, ClipboardList, Building2, Home, MoreHorizontal, Play, Check, SkipForward, SlidersHorizontal,  } from 'lucide-react';
import { MOCK_TASKS, FarmTask, TaskCategory, TaskStatus, CATEGORY_CONFIG, STATUS_CONFIG, PRIORITY_CONFIG,  } from './tasksData';
import TaskForm from './TaskForm';

// ─── Category icon map ────────────────────────────────────────────────────────
const CATEGORY_ICONS: Record<TaskCategory, React.ElementType> = {
  feeding: Wheat,
  health_check: Stethoscope,
  cleaning: Sparkles,
  vaccination: Syringe,
  egg_collection: Egg,
  weight_check: Scale,
  biosecurity: Shield,
  other: ClipboardList,
};

const STATUS_ICONS: Record<TaskStatus, React.ElementType> = {
  pending: Circle,
  in_progress: Clock,
  completed: CheckCircle2,
  overdue: AlertCircle,
  skipped: XCircle,
};

// ─── Task Card ────────────────────────────────────────────────────────────────
interface TaskCardProps {
  task: FarmTask;
  onStatusChange: (id: string, status: TaskStatus, notes?: string) => void;
  onEdit: (task: FarmTask) => void;
}

function TaskCard({ task, onStatusChange, onEdit }: TaskCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notesInput, setNotesInput] = useState('');
  const [showNotesInput, setShowNotesInput] = useState(false);

  const catCfg = CATEGORY_CONFIG[task.category];
  const statusCfg = STATUS_CONFIG[task.status];
  const priCfg = PRIORITY_CONFIG[task.priority];
  const CatIcon = CATEGORY_ICONS[task.category];
  const StatusIcon = STATUS_ICONS[task.status];

  function handleComplete() {
    if (task.notes || task.status === 'completed') {
      onStatusChange(task.id, 'completed');
    } else {
      setShowNotesInput(true);
      setExpanded(true);
    }
  }

  function submitComplete() {
    onStatusChange(task.id, 'completed', notesInput || undefined);
    setShowNotesInput(false);
    setNotesInput('');
  }

  return (
    <div
      className="card transition-all duration-200 hover:shadow-md"
      style={{
        borderLeft: `3px solid ${task.status === 'overdue' ? 'var(--danger)' : task.status === 'completed' ? 'var(--success)' : task.status === 'in_progress' ? 'var(--info)' : catCfg.color}`,
        opacity: task.status === 'completed' || task.status === 'skipped' ? 0.75 : 1,
      }}
    >
      {/* Card Header */}
      <div className="flex items-start gap-3 p-3">
        {/* Category icon */}
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: catCfg.bg }}
        >
          <CatIcon size={14} style={{ color: catCfg.color }} />
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-semibold text-foreground leading-tight"
                style={{ textDecoration: task.status === 'completed' ? 'line-through' : 'none', opacity: task.status === 'completed' ? 0.6 : 1 }}
              >
                {task.title}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {/* Time */}
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock size={10} />
                  {task.scheduledTime}
                  {task.completedAt && <span className="text-success" style={{ color: 'var(--success)' }}> → {task.completedAt}</span>}
                </span>
                {/* Category badge */}
                <span
                  className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: catCfg.bg, color: catCfg.color, fontSize: '0.65rem' }}
                >
                  {catCfg.label}
                </span>
                {/* Priority */}
                {(task.priority === 'high' || task.priority === 'critical') && (
                  <span className="text-xs font-semibold" style={{ color: priCfg.color, fontSize: '0.65rem' }}>
                    ● {priCfg.label}
                  </span>
                )}
              </div>
            </div>

            {/* Status + actions */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span
                className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                style={{ backgroundColor: statusCfg.bg, color: statusCfg.color, fontSize: '0.65rem' }}
              >
                <StatusIcon size={9} className="inline mr-1" />
                {statusCfg.label}
              </span>

              {/* Quick actions */}
              {task.status !== 'completed' && task.status !== 'skipped' && (
                <div className="flex items-center gap-0.5">
                  {task.status === 'pending' && (
                    <button
                      onClick={() => onStatusChange(task.id, 'in_progress')}
                      className="p-1 rounded hover:bg-muted transition-colors"
                      title="Start task"
                    >
                      <Play size={12} style={{ color: 'var(--info)' }} />
                    </button>
                  )}
                  {(task.status === 'pending' || task.status === 'in_progress' || task.status === 'overdue') && (
                    <button
                      onClick={handleComplete}
                      className="p-1 rounded hover:bg-muted transition-colors"
                      title="Mark complete"
                    >
                      <Check size={12} style={{ color: 'var(--success)' }} />
                    </button>
                  )}
                  <div className="relative">
                    <button
                      onClick={() => setMenuOpen(!menuOpen)}
                      className="p-1 rounded hover:bg-muted transition-colors"
                    >
                      <MoreHorizontal size={12} className="text-muted-foreground" />
                    </button>
                    {menuOpen && (
                      <div
                        className="absolute right-0 top-full mt-1 w-40 bg-card rounded-lg border shadow-modal py-1 z-20"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <button
                          onClick={() => { onEdit(task); setMenuOpen(false); }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors"
                        >
                          <ClipboardList size={11} /> Edit task
                        </button>
                        <button
                          onClick={() => { onStatusChange(task.id, 'skipped'); setMenuOpen(false); }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted transition-colors"
                          style={{ color: 'var(--warning)' }}
                        >
                          <SkipForward size={11} /> Skip task
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Expand toggle */}
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                {expanded ? <ChevronUp size={12} className="text-muted-foreground" /> : <ChevronDown size={12} className="text-muted-foreground" />}
              </button>
            </div>
          </div>

          {/* Assignee + farm row */}
          <div className="flex items-center gap-3 mt-1.5">
            <div className="flex items-center gap-1.5">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                style={{ backgroundColor: 'var(--primary)', fontSize: '0.55rem' }}
              >
                {task.assignedToInitials}
              </div>
              <span className="text-xs text-muted-foreground">{task.assignedTo}</span>
            </div>
            {task.farm && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Building2 size={10} />
                {task.farm}
              </span>
            )}
            {task.house && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Home size={10} />
                {task.house}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 border-t pt-2.5" style={{ borderColor: 'var(--border)' }}>
          <p className="text-xs text-muted-foreground leading-relaxed mb-2">{task.description}</p>
          {task.notes && (
            <div className="p-2 rounded-lg text-xs mb-2" style={{ backgroundColor: 'var(--muted)', color: 'var(--foreground)' }}>
              <span className="font-semibold">Notes: </span>{task.notes}
            </div>
          )}
          {task.flock && (
            <p className="text-xs text-muted-foreground mb-2">Flock: <span className="font-medium text-foreground">{task.flock}</span></p>
          )}

          {/* Notes input for completion */}
          {showNotesInput && (
            <div className="mt-2">
              <textarea
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                placeholder="Add completion notes (optional)..."
                className="form-input text-xs w-full resize-none"
                rows={2}
              />
              <div className="flex gap-2 mt-1.5">
                <button onClick={submitComplete} className="btn-primary text-xs py-1 px-3">
                  <Check size={11} /> Mark Complete
                </button>
                <button onClick={() => setShowNotesInput(false)} className="btn-secondary text-xs py-1 px-3">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Content ─────────────────────────────────────────────────────────────
export default function TasksContent() {
  const [tasks, setTasks] = useState<FarmTask[]>(MOCK_TASKS);
  const [formOpen, setFormOpen] = useState(false);
  const [editTask, setEditTask] = useState<FarmTask | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterFarm, setFilterFarm] = useState<string>('all');
  const [filterAssignee, setFilterAssignee] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');
  const [showFilters, setShowFilters] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  // Stats
  const todayTasks = tasks.filter((t) => t.date === today);
  const completed = todayTasks.filter((t) => t.status === 'completed').length;
  const inProgress = todayTasks.filter((t) => t.status === 'in_progress').length;
  const overdue = todayTasks.filter((t) => t.status === 'overdue').length;
  const pending = todayTasks.filter((t) => t.status === 'pending').length;
  const completionRate = todayTasks.length > 0 ? Math.round((completed / todayTasks.length) * 100) : 0;

  // Unique assignees
  const assignees = Array.from(new Set(tasks.map((t) => t.assignedTo)));

  // Filtered tasks
  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      const matchStatus = filterStatus === 'all' || t.status === filterStatus;
      const matchCat = filterCategory === 'all' || t.category === filterCategory;
      const matchFarm = filterFarm === 'all' || t.farm === filterFarm;
      const matchAssignee = filterAssignee === 'all' || t.assignedTo === filterAssignee;
      const matchSearch = !searchQuery || t.title.toLowerCase().includes(searchQuery.toLowerCase()) || t.assignedTo.toLowerCase().includes(searchQuery.toLowerCase());
      return matchStatus && matchCat && matchFarm && matchAssignee && matchSearch;
    }).sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));
  }, [tasks, filterStatus, filterCategory, filterFarm, filterAssignee, searchQuery]);

  // Board columns
  const boardColumns: { key: TaskStatus; label: string; color: string }[] = [
    { key: 'overdue',     label: 'Overdue',     color: 'var(--danger)' },
    { key: 'pending',     label: 'Pending',     color: 'var(--muted-foreground)' },
    { key: 'in_progress', label: 'In Progress', color: 'var(--info)' },
    { key: 'completed',   label: 'Completed',   color: 'var(--success)' },
  ];

  function handleStatusChange(id: string, status: TaskStatus, notes?: string) {
    setTasks((prev) => prev.map((t) => {
      if (t.id !== id) return t;
      return {
        ...t,
        status,
        completedAt: status === 'completed' ? new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : t.completedAt,
        notes: notes ?? t.notes,
      };
    }));
  }

  function handleSave(task: FarmTask) {
    if (editTask) {
      setTasks((prev) => prev.map((t) => t.id === task.id ? task : t));
    } else {
      setTasks((prev) => [task, ...prev]);
    }
    setFormOpen(false);
    setEditTask(null);
  }

  function handleEdit(task: FarmTask) {
    setEditTask(task);
    setFormOpen(true);
  }

  return (
    <div className="px-4 md:px-6 xl:px-8 py-5 max-w-screen-2xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CheckSquare size={20} style={{ color: 'var(--primary)' }} />
            <h1 className="text-2xl font-bold text-foreground">Daily Task Board</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Assign, track, and close farm staff tasks — feeding, health checks, cleaning, vaccinations
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={() => setViewMode('board')}
              className="px-3 py-1.5 text-xs font-medium transition-colors"
              style={{ backgroundColor: viewMode === 'board' ? 'var(--primary)' : 'transparent', color: viewMode === 'board' ? '#fff' : 'var(--muted-foreground)' }}
            >
              Board
            </button>
            <button
              onClick={() => setViewMode('list')}
              className="px-3 py-1.5 text-xs font-medium transition-colors"
              style={{ backgroundColor: viewMode === 'list' ? 'var(--primary)' : 'transparent', color: viewMode === 'list' ? '#fff' : 'var(--muted-foreground)' }}
            >
              List
            </button>
          </div>
          <button
            onClick={() => { setEditTask(null); setFormOpen(true); }}
            className="btn-primary text-sm"
          >
            <Plus size={15} />
            New Task
          </button>
        </div>
      </div>

      {/* KPI Summary Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[
          { label: "Today's Tasks", value: todayTasks.length, color: 'var(--primary)', bg: '#f0fdf4', icon: CheckSquare },
          { label: 'Completed', value: completed, color: 'var(--success)', bg: 'var(--success-bg)', icon: CheckCircle2 },
          { label: 'In Progress', value: inProgress, color: 'var(--info)', bg: 'var(--info-bg)', icon: Clock },
          { label: 'Overdue', value: overdue, color: 'var(--danger)', bg: '#fef2f2', icon: AlertCircle },
          { label: 'Completion Rate', value: `${completionRate}%`, color: completionRate >= 75 ? 'var(--success)' : completionRate >= 50 ? 'var(--warning)' : 'var(--danger)', bg: completionRate >= 75 ? 'var(--success-bg)' : completionRate >= 50 ? 'var(--warning-bg)' : '#fef2f2', icon: CheckSquare },
        ].map((s, i) => {
          const SIcon = s.icon;
          return (
            <div key={i} className="kpi-card flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.bg }}>
                <SIcon size={16} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">{s.label}</p>
                <p className="text-lg font-bold font-tabular text-foreground">{s.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="card p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-foreground">Today&apos;s Progress</span>
          <span className="text-xs text-muted-foreground">{completed} of {todayTasks.length} tasks completed</span>
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--muted)' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${completionRate}%`, backgroundColor: completionRate >= 75 ? 'var(--success)' : completionRate >= 50 ? 'var(--warning)' : 'var(--danger)' }}
          />
        </div>
        <div className="flex items-center gap-4 mt-2">
          {[
            { label: 'Completed', count: completed, color: 'var(--success)' },
            { label: 'In Progress', count: inProgress, color: 'var(--info)' },
            { label: 'Pending', count: pending, color: 'var(--muted-foreground)' },
            { label: 'Overdue', count: overdue, color: 'var(--danger)' },
          ].map((s) => (
            <span key={s.label} className="flex items-center gap-1 text-xs text-muted-foreground">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
              {s.label}: <span className="font-semibold text-foreground">{s.count}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Search + Filters */}
      <div className="card p-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search tasks or staff..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input text-sm pl-8 py-1.5 h-8 w-full"
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1.5 btn-secondary text-xs py-1.5 px-3 h-8"
          >
            <SlidersHorizontal size={12} />
            Filters
            {(filterStatus !== 'all' || filterCategory !== 'all' || filterFarm !== 'all' || filterAssignee !== 'all') && (
              <span className="w-4 h-4 rounded-full text-white flex items-center justify-center" style={{ backgroundColor: 'var(--primary)', fontSize: '0.6rem' }}>
                !
              </span>
            )}
          </button>

          <span className="ml-auto text-xs text-muted-foreground">{filtered.length} task{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Status</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="form-input text-xs py-1 px-2 h-7">
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="overdue">Overdue</option>
                <option value="skipped">Skipped</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Category</label>
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="form-input text-xs py-1 px-2 h-7">
                <option value="all">All</option>
                <option value="feeding">Feeding</option>
                <option value="health_check">Health Check</option>
                <option value="cleaning">Cleaning</option>
                <option value="vaccination">Vaccination</option>
                <option value="egg_collection">Egg Collection</option>
                <option value="weight_check">Weight Check</option>
                <option value="biosecurity">Biosecurity</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Farm</label>
              <select value={filterFarm} onChange={(e) => setFilterFarm(e.target.value)} className="form-input text-xs py-1 px-2 h-7">
                <option value="all">All Farms</option>
                <option value="Idudi Poultry Farm">Idudi Poultry Farm</option>
                <option value="Kampala Demo Farm">Kampala Demo Farm</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Assignee</label>
              <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} className="form-input text-xs py-1 px-2 h-7">
                <option value="all">All Staff</option>
                {assignees.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            {(filterStatus !== 'all' || filterCategory !== 'all' || filterFarm !== 'all' || filterAssignee !== 'all') && (
              <button
                onClick={() => { setFilterStatus('all'); setFilterCategory('all'); setFilterFarm('all'); setFilterAssignee('all'); }}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Board View */}
      {viewMode === 'board' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {boardColumns.map((col) => {
            const colTasks = filtered.filter((t) => t.status === col.key);
            return (
              <div key={col.key} className="flex flex-col gap-2">
                {/* Column header */}
                <div
                  className="flex items-center justify-between px-3 py-2 rounded-lg"
                  style={{ backgroundColor: STATUS_CONFIG[col.key].bg }}
                >
                  <span className="text-xs font-semibold" style={{ color: col.color }}>{col.label}</span>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: col.color, fontSize: '0.65rem' }}
                  >
                    {colTasks.length}
                  </span>
                </div>
                {/* Tasks */}
                <div className="flex flex-col gap-2">
                  {colTasks.length === 0 ? (
                    <div className="text-center py-6 text-xs text-muted-foreground">No tasks</div>
                  ) : (
                    colTasks.map((task) => (
                      <TaskCard key={task.id} task={task} onStatusChange={handleStatusChange} onEdit={handleEdit} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="flex flex-col gap-2">
          {filtered.length === 0 ? (
            <div className="card p-8 text-center text-muted-foreground text-sm">No tasks match your filters.</div>
          ) : (
            filtered.map((task) => (
              <TaskCard key={task.id} task={task} onStatusChange={handleStatusChange} onEdit={handleEdit} />
            ))
          )}
        </div>
      )}

      {/* Task Form Modal */}
      {formOpen && (
        <TaskForm
          open={formOpen}
          editTask={editTask}
          onClose={() => { setFormOpen(false); setEditTask(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
